import React from 'react';
import Input from '@components/common/Input';
import Select from '@components/common/Select';
import type { QueryFilter, QueryFieldType } from '@app-types';
import styles from './FilterValueInput.module.css';

interface FilterValueInputProps {
  /** The filter row whose value is being edited (supplies value, valueTo, operator). */
  filter: QueryFilter;
  /** The field's data type — decides which control renders. */
  fieldType: QueryFieldType;
  /** Allowed values for an `enum` field; empty for other types. */
  options: string[];
  /** Apply a partial update to the filter row (value / valueTo). */
  onChange: (patch: Partial<QueryFilter>) => void;
}

/**
 * Renders the value control for a query filter row, keyed off the field's type
 * and the chosen operator. Single source of truth shared by the query builder
 * and the results-page ad-hoc filter bar so the two never drift (#884):
 *   - "Is empty" / "Is not empty" are unary → no control
 *   - "Date range" → two date inputs (from / to, each bounding the other)
 *   - boolean → True/False Select
 *   - enum → Select of the field's options (value === label; rendered as-is)
 *   - else → typed Input (number / date / text)
 */
export const FilterValueInput: React.FC<FilterValueInputProps> = ({ filter, fieldType, options, onChange }) => {
  // "Is empty" / "Is not empty" are unary — they test for presence and take no value.
  if (filter.operator === 'isEmpty' || filter.operator === 'isNotEmpty') return null;

  if (filter.operator === 'dateRange') {
    const from = String(filter.value ?? '');
    const to = String(filter.valueTo ?? '');
    return (
      <div className={styles.rangeInputs}>
        <Input
          type="date"
          value={from}
          max={to || undefined}
          onChange={(e) => onChange({ value: e.target.value })}
        />
        <span>to</span>
        <Input
          type="date"
          value={to}
          min={from || undefined}
          onChange={(e) => onChange({ valueTo: e.target.value })}
        />
      </div>
    );
  }

  if (fieldType === 'boolean') {
    return (
      <Select
        options={[
          { value: 'true', label: 'True' },
          { value: 'false', label: 'False' }
        ]}
        value={String(filter.value ?? 'true')}
        onChange={(value) => onChange({ value: value === 'true' })}
      />
    );
  }

  if (fieldType === 'enum') {
    return (
      <Select
        options={options.map((opt) => ({ value: opt, label: opt }))}
        value={String(filter.value ?? options[0] ?? '')}
        onChange={(value) => onChange({ value })}
      />
    );
  }

  return (
    <Input
      type={inputTypeForField(fieldType)}
      value={String(filter.value ?? '')}
      onChange={(e) => onChange({ value: e.target.value })}
      placeholder="Value"
    />
  );
};

/** Maps a field type to the plain-Input's HTML input type (boolean/enum are handled above via Select). */
function inputTypeForField(fieldType: QueryFieldType): 'number' | 'date' | 'text' {
  if (fieldType === 'number') return 'number';
  if (fieldType === 'date') return 'date';
  return 'text';
}
